import classNames from "classnames";
import EmptyState from "@/components/EmptyState";
import Progress from "@/components/Progress";
import Tag from "@/components/Tag";
import SkeletonElement from "@/components/Skeleton/SkeletonElement";
import type { DashboardPacingRow } from "../_utils/dashboardPacing";
import { DASHBOARD_PANEL_SHELL_CLASS } from "../_utils/dashboardPanelStyles";

export type DashboardPacingPanelProps = {
  title: string;
  progress: DashboardPacingRow[];
  testId: string;
  audienceLabel?: string;
  loading?: boolean;
  error?: boolean;
  unavailable?: boolean;
};

function PacingRow({ progress }: { progress: DashboardPacingRow }) {
  return (
    <div className="pb-5 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-body-medium-semibold text-sys-brand-on-surface-variant">{progress.label}</span>
        <span className="text-body-large-bold text-sys-brand-on-surface">{progress.percent}%</span>
      </div>
      <div data-testid={`${progress.id}-progress-bar`}>
        <Progress variant="linear" value={progress.percent / 100} className="mt-2" />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-body-small text-sys-brand-on-surface-variant">
          {progress.completed.toLocaleString()} of {progress.total.toLocaleString()}{" "}
          {progress.detailLabel ?? "on target"}
        </p>
        {progress.statusLabel ? (
          <Tag
            tagType={
              progress.status === "INCOMPLETE" ? "warn" : progress.status === "NO_DATA" ? "information" : "success"
            }
            tagStyle="standard"
            size="small"
          >
            {progress.statusLabel}
          </Tag>
        ) : null}
      </div>
    </div>
  );
}

function PacingSkeleton({ testId }: { testId: string }) {
  return (
    <div className="flex flex-col gap-5 px-4 pb-4" data-testid={`${testId}-loading`}>
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <SkeletonElement height="h-5" width="w-1/3" rounded="rounded" />
          <SkeletonElement height="h-3" width="w-full" rounded="rounded-full" />
          <SkeletonElement height="h-4" width="w-1/2" rounded="rounded" />
        </div>
      ))}
    </div>
  );
}

export default function DashboardPacingPanel({
  title,
  progress,
  testId,
  audienceLabel = "Associate",
  loading = false,
  error = false,
  unavailable = false
}: DashboardPacingPanelProps) {
  return (
    <section data-testid={testId} className={classNames(DASHBOARD_PANEL_SHELL_CLASS, "overflow-hidden")}>
      <div className="flex w-full items-center justify-between gap-2 px-4 pb-3 pt-4 lg:py-3">
        <h2 className="text-title-medium-bold text-sys-brand-on-surface">{title}</h2>
      </div>
      {loading ? (
        <PacingSkeleton testId={testId} />
      ) : unavailable ? (
        <div data-testid={`${testId}-unavailable`}>
          <EmptyState
            iconName="lock"
            title={`${audienceLabel} Pacing Unavailable`}
            caption={`You do not have permission to view Career Services ${audienceLabel.toLowerCase()} pacing.`}
          />
        </div>
      ) : error ? (
        <div data-testid={`${testId}-error`}>
          <EmptyState
            iconName="error"
            title={`Unable to Load ${audienceLabel} Pacing`}
            caption="Try refreshing the page. If the problem continues, contact support."
          />
        </div>
      ) : progress.length > 0 ? (
        <div className="px-4 pb-4">
          {progress.map((row) => (
            <PacingRow key={row.id} progress={row} />
          ))}
        </div>
      ) : (
        <div data-testid={`${testId}-empty`}>
          <EmptyState title={`No ${audienceLabel} Pacing Data`} />
        </div>
      )}
    </section>
  );
}
